import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Database, Server } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BigDataProjectCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './BigDataProjectCostCalculatorPage/components/ResultsOverview';
import CostBreakdown from './BigDataProjectCostCalculatorPage/components/CostBreakdown';
import QuickEstimatorSidebar from './BigDataProjectCostCalculatorPage/components/QuickEstimatorSidebar';
import SavedProjectsSidebar from './BigDataProjectCostCalculatorPage/components/SavedProjectsSidebar';
import BestPracticesSidebar from './BigDataProjectCostCalculatorPage/components/BestPracticesSidebar';
import InfoContent from './BigDataProjectCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  dataVolume: '1000',             // в ГБ
  dataVolumeUnit: 'gb',           // 'gb', 'tb', 'pb'
  dataProcessingRate: '100',      // в ГБ/час
  projectDuration: '6',           // в месяцах
  dataSourcesCount: '3',          // количество источников данных
  projectType: 'analytics',       // тип проекта: analytics, ml, realtime, datawarehouse
  cloudProvider: 'aws',           // aws, azure, gcp, onpremise
  teamSize: '5',                  // количество специалистов
  dataComplexity: 'medium',       // low, medium, high
  securityLevel: 'standard',      // basic, standard, advanced
  includeMaintenanceCosts: true,  // включать расходы на поддержку
  maintenancePeriod: '12',        // период поддержки в месяцах
  includeTrainingCosts: false,    // включать расходы на обучение
  trainingPeopleCount: '10',      // количество обучаемых людей
  includeLicenseCosts: true,      // включать расходы на лицензии
  licenseType: 'per_user',        // per_user, per_core, per_cluster, subscription
  licenseCount: '10',             // количество лицензий
  includeConsultingCosts: false,  // включать расходы на консалтинг
  consultingDays: '10',           // количество дней консалтинга
  region: 'us',                   // us, eu, asia
  currency: 'usd',                // usd, eur, rub
  contingencyPercent: '10',       // процент на непредвиденные расходы
};

// Определение типов проектов Big Data
export const projectTypes = [
  { id: 'analytics', name: 'Аналитика данных', defaultTeamSize: 5, description: 'Сбор, обработка и анализ данных для получения бизнес-инсайтов' },
  { id: 'ml', name: 'Машинное обучение', defaultTeamSize: 7, description: 'Построение моделей машинного обучения для предсказания и классификации' },
  { id: 'realtime', name: 'Обработка в реальном времени', defaultTeamSize: 6, description: 'Системы для стриминговой обработки данных в реальном времени' },
  { id: 'datawarehouse', name: 'Хранилище данных', defaultTeamSize: 4, description: 'Создание централизованного хранилища данных для компании' },
  { id: 'dataquality', name: 'Управление качеством данных', defaultTeamSize: 3, description: 'Системы для очистки, валидации и улучшения качества данных' },
  { id: 'dataintegration', name: 'Интеграция данных', defaultTeamSize: 4, description: 'Процессы ETL/ELT для объединения данных из разных источников' }
];

// Определение облачных провайдеров
export const cloudProviders = [
  { id: 'aws', name: 'Amazon Web Services (AWS)', costMultiplier: 1.0 },
  { id: 'azure', name: 'Microsoft Azure', costMultiplier: 1.05 },
  { id: 'gcp', name: 'Google Cloud Platform (GCP)', costMultiplier: 0.95 },
  { id: 'onpremise', name: 'On-premise решение', costMultiplier: 1.4 }
];

// Определение регионов размещения
export const regionOptions = [
  { id: 'us', name: 'США', costMultiplier: 1.0 },
  { id: 'eu', name: 'Европа', costMultiplier: 1.1 },
  { id: 'asia', name: 'Азия', costMultiplier: 0.9 },
  { id: 'russia', name: 'Россия', costMultiplier: 0.8 }
];

// Определение уровней сложности данных
export const dataComplexityLevels = [
  { id: 'low', name: 'Низкая', description: 'Структурированные данные из небольшого числа источников', costMultiplier: 0.8 },
  { id: 'medium', name: 'Средняя', description: 'Смешанные данные из разных источников', costMultiplier: 1.0 },
  { id: 'high', name: 'Высокая', description: 'Сложные неструктурированные данные из многих источников', costMultiplier: 1.4 }
];

// Определение уровней безопасности
export const securityLevels = [
  { id: 'basic', name: 'Базовый', description: 'Основные меры безопасности', costMultiplier: 1.0 },
  { id: 'standard', name: 'Стандартный', description: 'Отраслевые стандарты безопасности', costMultiplier: 1.2 },
  { id: 'advanced', name: 'Продвинутый', description: 'Усиленная безопасность для чувствительных данных', costMultiplier: 1.5 }
];

// Определение типов лицензий
export const licenseTypes = [
  { id: 'per_user', name: 'На пользователя', description: 'Лицензия на каждого пользователя системы' },
  { id: 'per_core', name: 'На ядро процессора', description: 'Лицензирование по количеству ядер процессора' },
  { id: 'per_cluster', name: 'На кластер', description: 'Лицензия на весь кластер независимо от размера' },
  { id: 'subscription', name: 'Подписка', description: 'Подписочная модель с ежемесячной оплатой' }
];

/**
 * BigDataProjectCostCalculatorPage Component - Калькулятор оценки стоимости проектов по Big Data.
 */
const BigDataProjectCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('big-data-cost-calculator') || {
    id: 'big-data-cost-calculator',
    name: 'Калькулятор стоимости Big Data проектов',
    metaTitle: 'Калькулятор стоимости Big Data проектов | Gensio',
    metaDescription: 'Детальная оценка затрат на проекты Big Data: инфраструктура, персонал, лицензии, облачные сервисы. Планируйте бюджет эффективно.',
    relatedTools: ['cloud-cost-calculator', 'roi-calculator', 'data-storage-calculator']
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости Big Data проектов | Gensio';
  const metaDescription = tool?.metaDescription || 'Детальная оценка затрат на проекты Big Data: инфраструктура, персонал, лицензии, облачные сервисы. Планируйте бюджет эффективно.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState({});
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedBigDataProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findProjectType = (id) => {
        const found = projectTypes.find(t => t.id === id);
        return found ? found.id : initialFormData.projectType;
      };

      switch (subToolId) {
        case 'analytics':
          newState.projectType = findProjectType('analytics');
          newState.teamSize = '5';
          break;
        case 'ml':
          newState.projectType = findProjectType('ml');
          newState.teamSize = '7';
          break;
        case 'realtime':
          newState.projectType = findProjectType('realtime');
          newState.teamSize = '6';
          newState.dataProcessingRate = '200';
          break;
        case 'aws':
          newState.cloudProvider = 'aws';
          break;
        case 'azure':
          newState.cloudProvider = 'azure';
          break;
        case 'gcp':
          newState.cloudProvider = 'gcp';
          break;
        case 'onpremise':
          newState.cloudProvider = 'onpremise';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCostBreakdown({});
    setErrors({});

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `Проект Big Data ${formData.dataVolume}${formData.dataVolumeUnit.toUpperCase()} на ${formData.projectDuration} мес.`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      costBreakdown: { ...costBreakdown }
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedBigDataProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedBigDataProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
      setCostBreakdown(projectToLoad.costBreakdown);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Database className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости Big Data проектов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              costBreakdown={costBreakdown}
              setCostBreakdown={setCostBreakdown}
              projectTypes={projectTypes}
              cloudProviders={cloudProviders}
              dataComplexityLevels={dataComplexityLevels}
              securityLevels={securityLevels}
              licenseTypes={licenseTypes}
              regionOptions={regionOptions}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <CostBreakdown 
                  costBreakdown={costBreakdown} 
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент о Big Data --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой оценки --- */}
            <QuickEstimatorSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              projectTypes={projectTypes}
              cloudProviders={cloudProviders}
            />

            {/* --- Панель сохраненных проектов --- */}
            <SavedProjectsSidebar 
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
            />

            {/* --- Лучшие практики --- */}
            <BestPracticesSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Cost Breakdown Table */
        .cost-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .cost-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default BigDataProjectCostCalculatorPage;